// react
import styled, { css } from 'styled-components';

// components
import { Avatar } from '@/components/atoms/Avatar/Avatar';

import { Icons } from '@/components/atoms/Icon';

/**
 * ProfileSummary shows the user's name, role, and avatar.
 */
export const ProfileSummary = ({
  imgSrc,
  fallback,
  name,
  role,
  size = 'sm',
  isText = true,
  mode = 'light',
  icon,
}: ProfileSummaryProps) => {
  return (
    <StyledProfileSummary>
      <Avatar src={imgSrc} fallback={fallback} size={size} icon={icon} />
      {isText && (
        <div>
          <Role size={size} mode={mode}>
            {role}
          </Role>
          {name && (
            <Name size={size} mode={mode}>
              {name}
            </Name>
          )}
        </div>
      )}
    </StyledProfileSummary>
  );
};

const StyledProfileSummary = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

interface SizeProps {
  size?: 'sm' | 'md' | 'lg';
  mode?: 'dark' | 'light';
}
const Name = styled.div<SizeProps>`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) =>
    props.mode === 'light'
      ? props.theme.color.neutral[90]
      : props.theme.color.neutral[10]};
  ${(props) => {
    switch (props.size) {
      case 'sm':
        return css`
          font: ${props.theme.typography.body.sm.regular};
        `;
      case 'md':
        return css`
          font: ${props.theme.typography.body.md.regular};
        `;
      case 'lg':
        return css`
          font: ${props.theme.typography.body.lg.regular};
        `;
    }
  }}
`;

const Role = styled.div<SizeProps>`
  color: ${(props) =>
    props.mode === 'light'
      ? props.theme.color.neutral[100]
      : props.theme.color.neutral[10]};
  ${(props) => {
    switch (props.size) {
      case 'sm':
        return css`
          font: ${props.theme.typography.body.sm.semibold};
        `;
      case 'md':
        return css`
          font: ${props.theme.typography.body.md.semibold};
        `;
      case 'lg':
        return css`
          font: ${props.theme.typography.body.lg.semibold};
        `;
    }
  }}
`;

interface ProfileSummaryProps {
  imgSrc?: string;
  fallback: string;
  name?: string;
  role: string;
  isText?: boolean;
  size?: 'sm' | 'md' | 'lg';
  mode?: 'dark' | 'light';
  icon?: Icons;
}

ProfileSummary.displayName = 'ProfileSummary';

export default ProfileSummary;
